import {
  GLP_SELECTION,
  isGuestLiabilityProtectionLocked,
} from '../../util/guestLiabilityProtection';
import { hasPremiumMembership } from '../../util/user';

/**
 * Resolves the LT+ subscription id for speculate/initiate calls.
 * Prefers an explicit pageData value over the mutable ref fallback.
 *
 * @param {string|undefined|null} pageDataLtPlusSubscriptionId
 * @param {string|undefined|null} refLtPlusSubscriptionId
 * @return {string|undefined}
 */
export const resolveLtPlusSubscriptionId = (
  pageDataLtPlusSubscriptionId,
  refLtPlusSubscriptionId
) => {
  if (typeof pageDataLtPlusSubscriptionId === 'string' && pageDataLtPlusSubscriptionId) {
    return pageDataLtPlusSubscriptionId;
  }

  if (typeof refLtPlusSubscriptionId === 'string' && refLtPlusSubscriptionId) {
    return refLtPlusSubscriptionId;
  }

  return undefined;
};

/**
 * During LT+ checkout only allow the intentional reprice that includes the subscription id.
 * Other triggers (e.g. fetchUser flipping GLP to the locked member state) would otherwise
 * send ltPlusSubscriptionId as transit `false` and flash the un-waived total.
 *
 * @param {boolean} isCompletingLtPlusSubscription
 * @param {string|undefined} resolvedLtPlusSubscriptionId
 * @return {boolean}
 */
export const shouldSkipSpeculationDuringLtPlus = (
  isCompletingLtPlusSubscription,
  resolvedLtPlusSubscriptionId
) => isCompletingLtPlusSubscription && !resolvedLtPlusSubscriptionId;

/**
 * Returns the transaction to use when speculation is skipped during LT+ checkout.
 *
 * @param {Object} options
 * @param {Object|null|undefined} options.postLtPlusSpeculatedTransaction
 * @param {Object|null|undefined} options.speculatedTransaction
 * @return {Object|null|undefined}
 */
export const getLtPlusSpeculationFallback = ({
  postLtPlusSpeculatedTransaction,
  speculatedTransaction,
}) => postLtPlusSpeculatedTransaction || speculatedTransaction;

/**
 * While the LT+ signup flow drives its own speculation, ignore field-change triggered
 * re-speculations to avoid duplicate initiate-privileged calls.
 *
 * @param {boolean} isCompletingLtPlusSubscription
 * @return {boolean}
 */
export const shouldSkipInsuranceSpeculationDuringLtPlus = isCompletingLtPlusSubscription =>
  isCompletingLtPlusSubscription;

/**
 * Guards GLP field-change speculation during LT+ checkout or for locked members.
 *
 * @param {Object} options
 * @param {boolean} options.isCompletingLtPlusSubscription
 * @param {string|null|undefined} options.ltPlusSubscriptionId
 * @param {Object} options.currentUser
 * @return {boolean}
 */
export const shouldSkipGlpFieldChangeSpeculation = ({
  isCompletingLtPlusSubscription,
  ltPlusSubscriptionId,
  currentUser,
}) =>
  isCompletingLtPlusSubscription ||
  !!ltPlusSubscriptionId ||
  isGuestLiabilityProtectionLocked(currentUser);

/**
 * LT+ selection already has a local checkout preview and the actual membership flow
 * performs its own re-price after signup. Skipping speculation avoids an extra
 * initiate-privileged request before the user submits payment.
 *
 * @param {Object} currentUser
 * @param {string|undefined} guestLiabilityProtection
 * @return {boolean}
 */
export const shouldSkipLtPlusSelectionSpeculation = (currentUser, guestLiabilityProtection) =>
  guestLiabilityProtection === GLP_SELECTION.LT_PLUS && !hasPremiumMembership(currentUser);

/**
 * Keeps the user's original LT+ selection for the visible form/breakdown.
 *
 * @param {Object} pageData
 * @return {Object}
 */
export const buildLtPlusCompletionPageData = pageData => ({
  ...pageData,
  guestLiabilityProtection: GLP_SELECTION.LT_PLUS,
});

/**
 * Builds speculate params after LT+ signup. The visible form keeps LT+ selection, but the
 * pricing call uses GLP yes so the member waiver can zero out the line item.
 *
 * @param {Object} options
 * @param {Object} options.pageData
 * @param {string} options.ltPlusSubscriptionId
 * @param {Object} options.currentUser
 * @param {*} options.tripInsurance
 * @param {string|null} options.promoCode
 * @return {Object}
 */
export const buildLtPlusCompletionSpeculateParams = ({
  pageData,
  ltPlusSubscriptionId,
  currentUser,
  tripInsurance,
  promoCode,
}) => ({
  ...buildLtPlusCompletionPageData(pageData),
  guestLiabilityProtection: GLP_SELECTION.YES,
  tripInsurance,
  promoCode,
  currentUser: pageData.currentUser || currentUser,
  ltPlusSubscriptionId,
});

/**
 * Resolves the transaction used for final checkout submit.
 *
 * @param {Object} options
 * @param {Object|null|undefined} options.postLtPlusSpeculatedTransaction
 * @param {Object|null|undefined} options.speculatedTransaction
 * @param {Object|null|undefined} options.storedTransaction
 * @return {Object|null|undefined}
 */
export const resolveCheckoutTransaction = ({
  postLtPlusSpeculatedTransaction,
  speculatedTransaction,
  storedTransaction,
}) => postLtPlusSpeculatedTransaction || speculatedTransaction || storedTransaction;
